import { api, erebus, FILE_TRANSFER_TIMEOUT_MS } from 'boot/axios';
import { defineStore } from 'pinia';
import type { definitions } from '@erebus/api-types';

// API types from generated Swagger spec
type AttachmentCountsRequest = definitions['models.AttachmentCountsRequest'];
type AttachmentCountsResponse = definitions['models.AttachmentCountsResponse'];

/**
 * Pulls the filename out of a Content-Disposition header.
 * Returns null when the header is missing one, rather than throwing.
 *
 * The `filename*` parameter is preferred because it carries the real UTF-8 name.
 * The plain `filename` is quoted and ASCII-only, so accented characters arrive
 * as underscores. The unquoted branch is last, for older endpoints that still
 * send a bare name.
 */
const parseContentDispositionFileName = (contentDisposition: string): string | null => {
  const utf8 = /filename\*=\s*UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim()) || null;
    } catch {
      // A malformed percent-escape. Fall through to the plain filename below.
    }
  }

  // Inside the quotes, `\"` is an escaped quote and does not end the value.
  const quoted = /filename=\s*"((?:[^"\\]|\\.)*)"/i.exec(contentDisposition);
  if (quoted) {
    return quoted[1].replace(/\\(.)/g, '$1').trim() || null;
  }

  const bare = /filename=\s*([^";]+)/i.exec(contentDisposition);
  return bare ? bare[1].trim() || null : null;
};

export const fetchAudioAttachment = async (attachment: Attachment): Promise<ArrayBuffer> => {
  const headers = {
    'Content-Type': 'audio/mp3',
    Accept: 'audio/mp3',
  };
  const res = await api.get(
    `api/v1/activations/${attachment.attachmentSourceID}/attachments/${attachment.attachmentID}`,
    {
      responseType: 'arraybuffer',
      headers: headers,
    },
  );
  return res.data;
};

export const useAttachmentStore = defineStore({
  id: 'attachments',
  state: () => ({
    caseAttachments: [] as CaseAttachment[],
    attachmentCounts: {} as Record<string, number>,
  }),
  getters: {
    getCaseAttachments:
      (state) =>
      (caseID: string): CaseAttachment[] | undefined => {
        return state.caseAttachments.filter((ca: CaseAttachment) => {
          return ca.caseID.toLowerCase() === caseID.toLowerCase();
        });
      },
    getAttachmentCount:
      (state) =>
      (caseID: string): number => {
        if (!caseID) return 0;
        return state.attachmentCounts[caseID.toLowerCase()] || 0;
      },
    hasAttachmentCount:
      (state) =>
      (caseID: string): boolean => {
        if (!caseID) return false;
        return caseID.toLowerCase() in state.attachmentCounts;
      },
    getAttachmentUploadURL: (): string => {
      return `${erebus.defaults.baseURL}/attachments/`;
    },
  },
  actions: {
    saveCaseAttachments(caseAttachments: CaseAttachment[]): void {
      if (caseAttachments.length === 0) return;
      // remove existing attachments for this case
      this.caseAttachments = this.caseAttachments.filter(
        (a: CaseAttachment): boolean => a.caseID !== caseAttachments[0].caseID,
      );
      this.caseAttachments.push(...caseAttachments);
    },
    async deleteCaseAttachment(attachment: CaseAttachment): Promise<void> {
      await erebus.delete(`/attachments/${attachment.ID}`);
      await this.fetchCaseAttachments(attachment.caseID);
    },
    async fetchCaseAttachments(caseID: string): Promise<void> {
      const res = await erebus.get(`/attachments/case/${caseID}`);
      this.saveCaseAttachments(res.data);
    },
    async fetchCaseAttachment(attachmentID: string): Promise<CaseAttachmentFile> {
      // A 404 here means the attachment is gone - usually archived by another
      // user since this page was loaded. The interceptor in boot/axios.js
      // raises NotFoundError for it, which callers catch.
      const res = await erebus.get(`/attachments/${attachmentID}`, {
        responseType: 'arraybuffer',
        timeout: FILE_TRANSFER_TIMEOUT_MS,
      });

      const contentDisposition = res.headers['content-disposition'] || '';
      const fileName = parseContentDispositionFileName(contentDisposition);
      const resMimeType = res.headers['content-type'] || 'application/octet-stream';

      return {
        data: res.data,
        fileName: fileName,
        mimeType: resMimeType.toString(),
      };
    },
    async uploadCaseAttachment(
      file: File,
      attachmentDescription: CaseAttachmentDescription,
    ): Promise<void> {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', JSON.stringify(attachmentDescription));

      await erebus.post(`/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: FILE_TRANSFER_TIMEOUT_MS,
      });
    },
    async fetchAttachmentCounts(caseIDs: string[]): Promise<void> {
      if (caseIDs.length === 0) return;

      const request: AttachmentCountsRequest = {
        caseIDs: caseIDs,
      };

      const res = await erebus.post<AttachmentCountsResponse>('/attachments/counts', request);

      // Normalize keys to lowercase to match getter expectations
      const normalizedCounts: Record<string, number> = {};
      for (const [caseID, count] of Object.entries(res.data.counts)) {
        normalizedCounts[caseID.toLowerCase()] = count;
      }

      this.attachmentCounts = { ...this.attachmentCounts, ...normalizedCounts };
    },
  },
});

export const getCaseAttachments = async (caseID: number): Promise<CaseAttachment> => {
  const res = await erebus.get(`/attachments/case/${caseID}`);
  return res.data;
};

export type Attachment = {
  attachmentDate: number;
  attachmentFileName: string;
  attachmentID: number;
  attachmentName: string;
  attachmentSourceID: number;
  attachmentType: string;
  employeeID: number;
  isRead: boolean;
  isSelf: boolean;
};
